import { supabase } from '../lib/supabase';
import { ProductReview, ReviewToken, ReviewMedia } from '../types';

export const reviewService = {
    // ---------------------------------------------------------
    // ADMIN FUNCTIONS
    // ---------------------------------------------------------
    
    async generateToken(): Promise<string> {
        const { data, error } = await supabase
            .from('review_tokens')
            .insert([{}]) // We insert an empty object to use default values (uuid and dates)
            .select('token')
            .single();

        if (error) {
            console.error('Error generating token:', error);
            throw new Error('No se pudo generar el token de review.');
        }

        return data.token;
    },

    /**
     * Gets all reviews (approved and pending) for admin management.
     */
    async getAllReviews(): Promise<ProductReview[]> {
        const { data, error } = await supabase
            .from('product_reviews')
            .select(`
                *,
                media:review_media(*)
            `)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching all reviews:', error);
            return [];
        }

        return data || [];
    },

    /**
     * Updates the status of a review.
     */
    async updateReviewStatus(reviewId: string, status: 'approved' | 'rejected' | 'pending'): Promise<void> {
        const { error } = await supabase
            .from('product_reviews')
            .update({ status })
            .eq('id', reviewId);

        if (error) {
            console.error('Error updating review status:', error);
            throw error;
        }
    },

    /**
     * Deletes a review and its media records.
     * Note: Storage files are not deleted automatically in this simple implementation,
     * but media records in DB are deleted via CASCADE or manually.
     */
    async deleteReview(reviewId: string): Promise<void> {
        const { error } = await supabase
            .from('product_reviews')
            .delete()
            .eq('id', reviewId);

        if (error) {
            console.error('Error deleting review:', error);
            throw error;
        }
    },

    // ---------------------------------------------------------
    // PUBLIC FUNCTIONS
    // ---------------------------------------------------------

    /**
     * Validates if a token exists, is not used, and has not expired.
     */
    async validateToken(token: string): Promise<ReviewToken | null> {
        const { data, error } = await supabase
            .from('review_tokens')
            .select('*')
            .eq('token', token)
            .eq('is_used', false)
            .gt('expires_at', new Date().toISOString())
            .single();

        if (error) {
            console.error('Token validation failed:', error);
            return null; // Token is invalid, used, or expired
        }

        return data;
    },

    /**
     * Submits a review and uploads associated media.
     * 1. Inserts the review
     * 2. Uploads media files to bucket
     * 3. Inserts media records
     * 4. Marks the token as used
     */
    async submitReview(
        tokenId: string, 
        customerName: string, 
        rating: number, 
        comment: string, 
        files: File[]
    ): Promise<boolean> {
        try {
            // 1. Insert Review
            const { data: review, error: reviewError } = await supabase
                .from('product_reviews')
                .insert([{
                    token_id: tokenId,
                    customer_name: customerName,
                    rating,
                    comment: comment.trim() || null,
                    status: 'pending' // Default status
                }])
                .select('id')
                .single();

            if (reviewError) throw reviewError;

            // 2 & 3. Upload Media and Insert Media Records
            if (files && files.length > 0) {
                const mediaPromises = files.map(async (file) => {
                    // Generate a unique file name
                    const fileExt = file.name.split('.').pop() || 'webp';
                    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
                    const filePath = `general/${review.id}/${fileName}`;

                    const { error: uploadError } = await supabase.storage
                        .from('reviews_media')
                        .upload(filePath, file, {
                            cacheControl: '3600',
                            upsert: false
                        });

                    if (uploadError) throw uploadError;

                    // Get public URL
                    const { data: { publicUrl } } = supabase.storage
                        .from('reviews_media')
                        .getPublicUrl(filePath);

                    // Insert media record
                    const { error: mediaError } = await supabase
                        .from('review_media')
                        .insert([{
                            review_id: review.id,
                            media_url: publicUrl
                        }]);

                    if (mediaError) throw mediaError;
                });

                await Promise.all(mediaPromises);
            }

            // 4. Mark token as used
            const { error: tokenError } = await supabase
                .from('review_tokens')
                .update({ is_used: true })
                .eq('id', tokenId);

            if (tokenError) throw tokenError;

            return true;
        } catch (error) {
            console.error('Error submitting review:', error);
            return false;
        }
    },

    /**
     * Gets all approved reviews.
     * Includes associated media files.
     */
    async getAllApprovedReviews(): Promise<ProductReview[]> {
        const { data, error } = await supabase
            .from('product_reviews')
            .select(`
                *,
                media:review_media(*)
            `)
            .eq('status', 'approved')
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching reviews:', error);
            return [];
        }

        return data || [];
    },
    
    /**
     * Utility to sanitize and compress images to WebP via Canvas before uploading.
     * This destroys EXIF data and potential malicious payloads.
     */
    sanitizeImage(file: File, maxWidth = 1920, maxHeight = 1920, quality = 0.8): Promise<File> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            img.onload = () => {
                let { width, height } = img;
                
                // Calculate new dimensions while maintaining aspect ratio
                if (width > maxWidth) {
                    height = (height * maxWidth) / width;
                    width = maxWidth;
                }
                if (height > maxHeight) {
                    width = (width * maxHeight) / height;
                    height = maxHeight;
                }
                
                canvas.width = width;
                canvas.height = height;
                
                if (ctx) {
                    // Fill with white background in case of transparent PNGs converting to JPEG/WebP
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, width, height);
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + '.webp', {
                                type: 'image/webp',
                                lastModified: Date.now()
                            });
                            resolve(newFile);
                        } else {
                            reject(new Error('Canvas to Blob failed'));
                        }
                    }, 'image/webp', quality);
                } else {
                    reject(new Error('Canvas context not available'));
                }
            };
            
            img.onerror = () => {
                reject(new Error('Failed to load image for sanitization'));
            };
            
            img.src = URL.createObjectURL(file);
        });
    }
};
